import * as React from 'react';
import {ApolloConsumer} from 'react-apollo';

import type {DocumentNode} from 'graphql';
import * as Ramda from 'ramda';
import {Observable} from 'rxjs';

import type {EmailAutomationField} from 'nutshell-graphql-types';
import type {
    GetModifiedEmailAutomationsStatuses_emailAutomationStatusesModified_edges,
    GetEmailAutomationsNeedingAttention,
    GetEmailAutomationsReadyToSend,
    GetEmailAutomationsQueued,
    EmailAutomationStatusStatus as EmailAutomationStatus,
} from 'nutshell-graphql-types';

import {nutApolloClient} from '../../../apollo-client';

import {GetInstanceInfo} from '../../core/get-instance-info';

import {CancelEmailAutomation} from './cancel-email-automation';
import GET_MODIFIED_EMAIL_AUTOMATION_STATUSES from './graphql/queries/get-modified-email-automation-statuses.graphql';
import {UpdateEmailAutomationStatusField} from './update-email-automation-status-field';

const POLL_INTERVAL = 5000;
// Emit a tick once every 5 seconds, since we don't really update more often then that.
// But this guarantees we only have a single timer for all mounted timestamps.
const TIMER = Observable.interval(POLL_INTERVAL);

type OwnProps = {
    children: (arg1: {
        onCancel: (id: string, currentEmailAutomationStatus?: any) => Promise<any>;
        onUpdateStatusField: (
            emailAutomationStatus: any,
            field: EmailAutomationField,
            newValue?: string | null | undefined
        ) => Promise<any>;
        hasAutomatedEmailSequences: boolean;
        hasConfiguredAutomations: boolean;
    }) => React.ReactElement;
    query: DocumentNode;
    fetchLimit: number;
    validStatuses: EmailAutomationStatus[];
    // This is the key used to get the values into and out of the cache
    cacheKey:
        | keyof GetEmailAutomationsNeedingAttention
        | keyof GetEmailAutomationsReadyToSend
        | keyof GetEmailAutomationsQueued;
};

type FetchModifiedProps = {
    onCancel: (id: string, currentEmailAutomationStatus?: any) => Promise<any>;
    onUpdateStatusField: (
        emailAutomationStatus: any,
        field: EmailAutomationField,
        newValue?: string | null | undefined
    ) => Promise<any>;
    modifiedEdges: GetModifiedEmailAutomationsStatuses_emailAutomationStatusesModified_edges[];
    getModified: () => Promise<any>;
    hasAutomatedEmailSequences: boolean;
    hasConfiguredAutomations: boolean;
};

type Props = OwnProps & FetchModifiedProps;

/**
 * This really just contains the timer for running the query every 5 seconds. It
 * has to be outside of `AutomationListWrapper` becuase of the useEffect needed.
 */
function AutomationListWrapperGetModified(props: Props) {
    const {
        getModified,
        children,
        modifiedEdges,
        onCancel,
        onUpdateStatusField,
        query,
        hasAutomatedEmailSequences,
        hasConfiguredAutomations,
        fetchLimit,
        validStatuses,
        cacheKey,
    } = props;
    // @ts-expect-error
    const timerSubscription = React.useRef<rxjs.Subscription | null | undefined>(null);

    // Trigger a fetch for more modified rows.
    React.useEffect(() => {
        timerSubscription.current = TIMER.subscribe(() => {
            getModified();
        });

        return () => {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            timerSubscription.current && timerSubscription.current.unsubscribe();
        };
    }, [getModified]);

    // When we get new modified edges, merge them into our cache
    React.useEffect(() => {
        // Need to try/catch this, because trying to get a cache that doesn't exist throws an exception
        // https://github.com/apollographql/apollo-feature-requests/issues/1
        try {
            const existingCache = nutApolloClient.readQuery({
                query: query,
                // This needs to be the same as below.  The variables don't change in the cache when using
                // fetchMore and updateQuery
                variables: {limit: fetchLimit},
            });

            if (!existingCache) return;

            const existingEdges = existingCache[cacheKey].edges;
            // @ts-expect-error
            const existingIds = existingEdges.map((edge) => edge.node.id);

            const edgesToKeep = modifiedEdges.filter((edge) =>
                validStatuses.includes(edge.node.status)
            );
            const newEdges = edgesToKeep.filter((edge) => !existingIds.includes(edge.node.id));

            // TODO: insert into a somewhat-sorted spot in the list
            const mergedEdges = [...existingEdges, ...newEdges];

            nutApolloClient.writeQuery({
                query: query,
                variables: {limit: fetchLimit},
                data: {
                    ...existingCache,
                    [cacheKey]: {
                        ...existingCache[cacheKey],
                        edges: mergedEdges,
                    },
                },
            });
        } catch (_e: any) {
            // We get an error on the first call, because theres nothing in the
            // cache to get, but no reason to do anything with it
        }
        // Only modifiedEdges will actually change, but otherwise linting gets
        // pretty upset
    }, [modifiedEdges, fetchLimit, query, validStatuses, cacheKey]);

    return children({
        onCancel,
        onUpdateStatusField,
        // @ts-expect-error
        modifiedEdges,
        hasAutomatedEmailSequences,
        hasConfiguredAutomations,
    });
}

/**
 * This wrapper provides automation lists with a few props they can use to cancel
 * an emailAutomationStatus, and to trigger a request to get modified rows which
 * they can then use to merge into their appropriate cache.
 */
export function AutomationListWrapper(props: OwnProps) {
    const [cursor, setCursor] = React.useState<string | null | undefined>(null);
    const [modifiedEdges, setModifiedEdges] = React.useState<any[]>([]);

    return (
        <GetInstanceInfo>
            {({sessionInstance: instanceInfo}) => (
                <UpdateEmailAutomationStatusField>
                    {({onUpdateStatusField}) => (
                        <CancelEmailAutomation>
                            {({onCancel}) => (
                                <ApolloConsumer>
                                    {(client) => {
                                        return (
                                            <AutomationListWrapperGetModified
                                                {...props}
                                                hasAutomatedEmailSequences={
                                                    NutClientConfig.hasEmailSequences
                                                }
                                                hasConfiguredAutomations={Boolean(
                                                    instanceInfo &&
                                                        instanceInfo.hasConfiguredNutshellEmailAutomations
                                                )}
                                                getModified={() => {
                                                    return client
                                                        .query({
                                                            query: GET_MODIFIED_EMAIL_AUTOMATION_STATUSES,
                                                            variables: {
                                                                after: cursor,
                                                                // If we don't have a cursor yet, only fetch the most
                                                                // recently modified row to get our starting cursor.
                                                                last: cursor ? null : 1,
                                                            },
                                                            fetchPolicy: 'network-only',
                                                        })
                                                        .then(({data}) => {
                                                            const newData =
                                                                data &&
                                                                data.emailAutomationStatusesModified;
                                                            const newCursor =
                                                                newData &&
                                                                newData.pageInfo &&
                                                                newData.pageInfo.endCursor;
                                                            const changedEdges =
                                                                (newData && newData.edges) || [];
                                                            if (newCursor) {
                                                                // When polling for new entries, if there are no new
                                                                // entries we won't have an end cursor. We don't want to
                                                                // override our last cursor with null
                                                                setCursor(newCursor);
                                                            }
                                                            if (
                                                                cursor &&
                                                                !Ramda.equals(
                                                                    changedEdges,
                                                                    modifiedEdges
                                                                )
                                                            ) {
                                                                setModifiedEdges(changedEdges);
                                                            }
                                                        });
                                                }}
                                                onCancel={onCancel}
                                                onUpdateStatusField={onUpdateStatusField}
                                                modifiedEdges={modifiedEdges}
                                            />
                                        );
                                    }}
                                </ApolloConsumer>
                            )}
                        </CancelEmailAutomation>
                    )}
                </UpdateEmailAutomationStatusField>
            )}
        </GetInstanceInfo>
    );
}
